/**
 * purchase-options
 *
 * Custom subscribe / one-time purchase UI with quantity-break tiers.
 *
 * Seal Subscriptions renders its own radiogroup asynchronously into the product
 * information column. Rather than reimplementing its selling-plan logic, this
 * component hides that widget and clicks Seal's real radios, so the hidden
 * `selling_plan` input Seal maintains inside the product form stays authoritative.
 * If Seal never loads, the component falls back to writing `selling_plan` itself.
 */
(function () {
  if (customElements.get('purchase-options')) return;

  var SEAL_CONTAINER = '.sealsubs-container';
  var SEAL_OPTION = 'input.sls-option';

  function parseJSONAttribute(el, name) {
    try {
      return JSON.parse(el.getAttribute(name) || '{}');
    } catch (e) {
      console.error('[purchase-options] could not parse ' + name, e);
      return {};
    }
  }

  function clamp(value, min, max) {
    if (isNaN(value)) return min;
    return Math.min(Math.max(value, min), max);
  }

  class PurchaseOptions extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.form = document.getElementById(this.dataset.form);
      this.maxQuantity = parseInt(this.dataset.maxQuantity, 10) || 12;
      this.tier3Min = parseInt(this.dataset.tier3Min, 10) || 3;
      this.sellingPlanId = this.dataset.sellingPlan || '';
      this.oneTimePrices = parseJSONAttribute(this, 'data-onetime-prices');
      this.subscriptionPrices = parseJSONAttribute(this, 'data-subscription-prices');

      this.quantityInput = this.querySelector('[data-po-quantity]');
      this.cards = Array.from(this.querySelectorAll('[data-po-card]'));
      this.tiers = Array.from(this.querySelectorAll('[data-po-tier]'));

      var checkedCard = this.querySelector('[data-po-mode-input]:checked');
      this.mode = checkedCard ? checkedCard.value : 'one_time';
      this.tier = 1;
      this.subQuantity = 1;
      this.tierQuantity = this.tier3Min;

      this.bindEvents();
      this.watchSeal();
      this.render();

      // Seal can inject or rewrite its input after we last synced, so re-assert
      // the selection as the form submits. Capture phase runs before the theme's
      // own submit handler builds the FormData.
      if (this.form) {
        this.form.addEventListener('submit', () => {
          this.syncQuantity();
          this.syncSellingPlan();
        }, true);
      }
    }

    bindEvents() {
      this.addEventListener('change', (event) => {
        var target = event.target;

        if (target.matches('[data-po-mode-input]')) {
          this.mode = target.value;
          this.render();
        } else if (target.matches('[data-po-tier-input]')) {
          this.tier = parseInt(target.value, 10);
          this.render();
        } else if (target.matches('[data-po-step-input]')) {
          this.setStepperValue(target, parseInt(target.value, 10));
        }
      });

      // Clicking anywhere on a collapsed card selects it — the card head is the
      // only <label>, so nested tier labels stay valid HTML.
      this.addEventListener('click', (event) => {
        var stepButton = event.target.closest('[data-po-step]');
        if (stepButton) {
          var stepper = stepButton.closest('[data-po-stepper]');
          var input = stepper.querySelector('[data-po-step-input]');
          this.setStepperValue(input, parseInt(input.value, 10) + parseInt(stepButton.dataset.poStep, 10));
          return;
        }

        if (event.target.closest('label') || event.target.closest('input')) return;

        var tier = event.target.closest('[data-po-tier]');
        if (tier) {
          var tierInput = tier.querySelector('[data-po-tier-input]');
          if (tierInput && !tierInput.checked) {
            tierInput.checked = true;
            this.tier = parseInt(tierInput.value, 10);
            this.render();
          }
          return;
        }

        var card = event.target.closest('[data-po-card]');
        if (card) {
          var modeInput = card.querySelector('[data-po-mode-input]');
          if (modeInput && !modeInput.checked) {
            modeInput.checked = true;
            this.mode = modeInput.value;
            this.render();
          }
        }
      });
    }

    setStepperValue(input, value) {
      var min = parseInt(input.min, 10) || 1;
      var max = parseInt(input.max, 10) || this.maxQuantity;
      var next = clamp(value, min, max);
      input.value = next;

      var target = input.closest('[data-po-stepper]').dataset.target;
      if (target === 'subscription') {
        this.subQuantity = next;
      } else {
        this.tierQuantity = next;
      }
      this.render();
    }

    get quantity() {
      if (this.mode === 'subscription') return this.subQuantity;
      if (this.tier === 1) return 1;
      if (this.tier === 2) return 2;
      return this.tierQuantity;
    }

    render() {
      this.cards.forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.mode === this.mode);
      });
      this.tiers.forEach((tier) => {
        tier.classList.toggle('is-selected', parseInt(tier.dataset.tier, 10) === this.tier);
      });

      this.renderSubscriptionPrice();
      this.renderTier3Price();
      this.syncQuantity();
      this.syncSellingPlan();
    }

    renderSubscriptionPrice() {
      var prices = this.subscriptionPrices[this.subQuantity];
      if (!prices) return;

      var total = this.querySelector('[data-po-sub-total]');
      var compare = this.querySelector('[data-po-sub-compare]');

      if (total) total.textContent = prices.total_f;
      if (compare) compare.textContent = prices.gross_f;
    }

    renderTier3Price() {
      var tier = this.tiers.find((t) => parseInt(t.dataset.tier, 10) === 3);
      if (!tier) return;

      var prices = this.oneTimePrices[this.tierQuantity];
      if (!prices) return;

      var total = tier.querySelector('[data-po-tier-total]');
      var compare = tier.querySelector('[data-po-tier-compare]');
      var unit = tier.querySelector('[data-po-tier-unit]');

      if (total) total.textContent = prices.total_f;
      if (compare) compare.textContent = prices.gross_f;
      if (unit && unit.dataset.template) {
        unit.textContent = unit.dataset.template.replace('[unit]', prices.unit_f);
      }
      tier.dataset.quantity = this.tierQuantity;
    }

    syncQuantity() {
      if (!this.quantityInput) return;
      var quantity = this.quantity;
      if (this.quantityInput.value === String(quantity)) return;
      this.quantityInput.value = quantity;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Click Seal's own radio so the app's internal state follows the UI, then
     * verify the product form actually carries the right selling plan.
     *
     * Seal does not reliably mirror its selection into the form once its widget
     * is hidden, and a missing selling_plan silently turns a subscription into a
     * one-time purchase — so the form is checked rather than trusted.
     */
    syncSellingPlan() {
      var container = document.querySelector(SEAL_CONTAINER);
      var wanted = this.mode === 'subscription' ? this.sellingPlanId : '';

      if (container) {
        var selector = this.mode === 'subscription'
          ? SEAL_OPTION + '[data-type="subscription"]'
          : SEAL_OPTION + '[data-type="one_time"]';
        var radio = container.querySelector(selector);
        if (radio && !radio.checked) radio.click();
      }

      if (!this.form) return;

      var sealInput = this.form.querySelector('input[name="selling_plan"]:not([data-po-selling-plan])');

      if (sealInput) {
        // Seal owns an input in the form — correct it instead of adding a second.
        this.removeFallbackInput();
        if (sealInput.value !== wanted) sealInput.value = wanted;
        return;
      }

      if (!wanted) {
        this.removeFallbackInput();
        return;
      }

      if (!this.fallbackInput || !this.fallbackInput.isConnected) {
        this.fallbackInput = document.createElement('input');
        this.fallbackInput.type = 'hidden';
        this.fallbackInput.name = 'selling_plan';
        this.fallbackInput.setAttribute('data-po-selling-plan', '');
        this.form.appendChild(this.fallbackInput);
      }
      this.fallbackInput.value = wanted;
    }

    removeFallbackInput() {
      if (this.fallbackInput) {
        this.fallbackInput.remove();
        this.fallbackInput = null;
      }
    }

    /**
     * Seal injects and re-renders its widget asynchronously (including on variant
     * change), so keep it hidden and re-apply the current selection whenever it
     * reappears.
     */
    watchSeal() {
      var apply = () => {
        var container = document.querySelector(SEAL_CONTAINER);
        if (!container) return false;

        var block = container.closest('.shopify-app-block') || container.closest('.sealsubs-target-element') || container;
        block.classList.add('po-seal-hidden');
        this.syncSellingPlan();
        return true;
      };

      if (apply()) return;

      this.sealObserver = new MutationObserver(() => {
        if (apply()) {
          this.sealObserver.disconnect();
          this.sealObserver = null;
        }
      });
      this.sealObserver.observe(document.body, { childList: true, subtree: true });

      // Stop watching after 15s rather than observing the whole document forever.
      setTimeout(() => {
        if (this.sealObserver) {
          this.sealObserver.disconnect();
          this.sealObserver = null;
          this.syncSellingPlan();
        }
      }, 15000);
    }

    disconnectedCallback() {
      if (this.sealObserver) {
        this.sealObserver.disconnect();
        this.sealObserver = null;
      }
    }
  }

  customElements.define('purchase-options', PurchaseOptions);
})();
